/** @jsx jsx */

import type { CSSObject } from '@emotion/react';
import { Global, jsx } from '@emotion/react';
import { Fragment, memo } from 'react';
import type { ThemeProviderProps } from '@balance-web/theme';
import { FOCUS_RING, ThemeProvider, useRawTheme } from '@balance-web/theme';
import { MaxElevationProvider } from '@balance-web/elevate';

import { scopedAppResetStyles, standaloneAppResetStyles } from './reset-styles';
import type { LinkComponent } from './linkComponent';
import { DefaultLinkComponent, LinkComponentContext } from './linkComponent';
import { CoreContext } from './context';
import {
  setPortalParentElement,
  setPortalParentSelector,
} from './portalParenting';

// Core
// ------------------------------

export type CoreProps = ThemeProviderProps & {
  linkComponent: LinkComponent;
  globalStyles?: CSSObject;
  fastMode?: boolean;
  portalParent?: string;
  /** Direct element reference for portal rendering. Takes priority over `portalParent` selector. */
  portalParentElement?: Element;
  appType?: 'standalone' | 'r1-classic-bridge';
};

export const Core = ({
  children,
  linkComponent = DefaultLinkComponent,
  theme,
  themeRaw,
  themeCSSVars,
  globalStyles,
  fastMode,
  portalParent,
  portalParentElement,
  appType = 'standalone',
}: CoreProps) => {
  setPortalParentSelector(portalParent);
  setPortalParentElement(portalParentElement);

  return (
    <CoreContext.Provider value={{ fastMode }}>
      <LinkComponentContext.Provider value={linkComponent}>
        <MaxElevationProvider>
          <ThemeProvider
            theme={theme}
            themeCSSVars={themeCSSVars}
            themeRaw={themeRaw}
          >
            <GlobalStyles appType={appType} globalStyles={globalStyles} />
            <App>{children}</App>
          </ThemeProvider>
        </MaxElevationProvider>
      </LinkComponentContext.Provider>
    </CoreContext.Provider>
  );
};

// NOTE: only re-render leaf nodes that listen to theme changes

const App = memo((props) => {
  return <Fragment {...props} />;
});
App.displayName = 'App';

// Global styles
// ------------------------------

type GlobalStylesProps = Pick<CoreProps, 'appType' | 'globalStyles'>;

const WIDGET_WRAPPER_CLASS = '.reckon-widget-root';

const GlobalStyles = (props: GlobalStylesProps) => {
  const { semantic, typography, themeCSSVars } = useRawTheme();
  const isEmbeddedWidget = props.appType === 'r1-classic-bridge';

  // For embedded widgets the reset is scoped to the wrapper class so it never
  // leaks to the host page. For standalone apps the global reset is used.
  const resetStyles = isEmbeddedWidget
    ? scopedAppResetStyles(WIDGET_WRAPPER_CLASS)
    : standaloneAppResetStyles;

  // For embedded widgets, default styles are applied to the wrapper div rather
  // than `body` so they don't affect the host page.
  const defaultsSelector = isEmbeddedWidget ? WIDGET_WRAPPER_CLASS : 'body';

  return (
    <Fragment>
      <Global styles={resetStyles} />

      {/* focus ring */}
      <Global
        styles={{
          '*:focus': {
            outline: 0,
          },
          '*:focus-visible': {
            outline: `2px solid ${FOCUS_RING}`,
            outlineOffset: 2,
          },

          // remove firefox dotted focus-ring
          '& [type="button"]:-moz-focusring, [type="reset"]:-moz-focusring, [type="submit"]:-moz-focusring, button:-moz-focusring': {
            outline: 'none !important',
          },
        }}
      />

      {/* defaults */}
      <Global
        styles={[
          {
            [defaultsSelector]: {
              backgroundColor: semantic.colour.surface.neutral.background,
              fontFamily: typography.fontFamily.body, // TODO: remove

              // optimize legibility
              fontFeatureSettings: '"liga" 1', // Enable OpenType ligatures in IE
              textRendering: 'optimizeLegibility',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              MozFontFeatureSettings: '"liga" on',
            },
            // TODO: global "selection" styles?
          },
          props.globalStyles,
        ]}
      />

      {/* CSS design tokens */}
      <Global styles={themeCSSVars} />
    </Fragment>
  );
};
